import { Box, Divider, Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import { useAppSelector } from '@/shared/model';
import {
  Button,
  SearchInput,
  ColumnsList,
  ToggleButtonGroup,
  useFilterByNameOrGroup,
} from '@/shared/ui';
import { selectUserSettingsMeta } from '../../model/slice';
import type { CLSFilters } from '../../model/types';
import { CLSType } from '../../model/types';

interface Props {
  value: CLSFilters;
  onChange: (value: CLSFilters) => void;
}

const CLS_TYPE_OPTIONS = [
  {
    label: 'Blacklist',
    value: CLSType.BLACKLIST,
    tooltip: 'User can access all columns except the unselected ones',
  },
  {
    label: 'Whitelist',
    value: CLSType.WHITELIST,
    tooltip: 'User can access only the selected columns',
  },
];

export function CLSPanel({ value, onChange }: Props) {
  const userSettingsMeta = useAppSelector(selectUserSettingsMeta);
  const [searchQuery, setSearchQuery] = useState('');
  const allColumns = useMemo(() => {
    if (!userSettingsMeta) return [];

    return userSettingsMeta.clsColumns
      .filter((column) => !!userSettingsMeta.config[column])
      .map((column) => {
        const { name, description, group, order } =
          userSettingsMeta.config[column];

        return { name, description, value: column, group, order };
      });
  }, [userSettingsMeta]);
  const filteredColumns = useFilterByNameOrGroup(allColumns, searchQuery);
  const selectedColumns = useMemo(() => {
    if (!userSettingsMeta) {
      return [];
    }

    const columnSet = new Set(value.columns);

    if (value.type === CLSType.BLACKLIST) {
      return userSettingsMeta.clsColumns.filter((col) => !columnSet.has(col));
    } else {
      return userSettingsMeta.clsColumns.filter((col) => columnSet.has(col));
    }
  }, [value, userSettingsMeta]);

  const handleTypeChange = (newType: CLSType) => {
    const oldSet = new Set(value.columns);
    const invertedColumns =
      userSettingsMeta?.clsColumns.filter((col) => !oldSet.has(col)) ?? [];

    onChange({ type: newType, columns: invertedColumns });
  };
  const handleSelectedColumnsChange = (newCheckedColumns: string[]) => {
    const checkedSet = new Set(newCheckedColumns);

    const newColumns =
      value.type === CLSType.BLACKLIST
        ? userSettingsMeta?.clsColumns.filter((col) => !checkedSet.has(col)) ??
          []
        : newCheckedColumns;

    onChange({ ...value, columns: newColumns });
  };
  const handleClearAll = () => {
    const clearedColumns =
      value.type === CLSType.BLACKLIST
        ? userSettingsMeta?.clsColumns ?? []
        : [];

    onChange({ ...value, columns: clearedColumns });
  };
  const handleReset = () => {
    setSearchQuery('');
    const resetColumns =
      value.type === CLSType.WHITELIST
        ? userSettingsMeta?.clsColumns ?? []
        : [];
    onChange({ ...value, columns: resetColumns });
  };

  return (
    <Box
      component="ul"
      sx={{
        height: '100%',
        overflow: 'hidden',
        m: 0,
        p: 0,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Divider />
      <Box
        sx={{
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <ToggleButtonGroup
          fullWidth
          options={CLS_TYPE_OPTIONS}
          value={value.type}
          onChange={handleTypeChange}
        />
        <SearchInput query={searchQuery} onChange={setSearchQuery} />
      </Box>
      <Divider />
      <Box
        sx={{
          px: 2,
          py: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography
          component="span"
          variant="modalContent"
          sx={{ fontWeight: 500, color: '#7E828F' }}
        >
          Selected:{' '}
          <Box
            component="span"
            sx={{ color: (theme) => theme.palette.primary.main }}
          >
            {selectedColumns.length}
          </Box>{' '}
          of {userSettingsMeta?.clsColumns.length}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            sx={{ py: 0, lineHeight: '1.375rem', color: 'black' }}
            variant="text"
            color="secondary"
            onClick={handleClearAll}
          >
            Clear all
          </Button>
          <Button
            sx={{ py: 0, lineHeight: '1.375rem', color: 'black', minWidth: 0 }}
            variant="text"
            color="secondary"
            onClick={handleReset}
          >
            Reset
          </Button>
        </Box>
      </Box>
      <Divider />
      <Box sx={{ flexGrow: 1, display: 'flex' }}>
        <ColumnsList
          columns={filteredColumns}
          selectedColumns={selectedColumns}
          onSelectedColumnsChange={handleSelectedColumnsChange}
        />
      </Box>
    </Box>
  );
}
